import { useContext, useEffect, useMemo } from "react";
import { Cancel01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { Avatar, ButtonBase, Divider, Drawer, IconButton, Stack, Typography } from "@mui/material";
import { MENU_FLOWS, DINING_MODES } from "@/constants/global";
import { LocalStorage } from "@/constants/localStorage";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { setIsDineModeOpen } from "@/store/uiSlice";
import { WebsiteContext } from "@/pages/website/WebsiteProvider";
import camelCase from "lodash/camelcase";
import get from "lodash/get";
import { checkServiceAvailability } from "@/utils/weekDays";
import { setOrdersFlow } from "@/features/order/slice";
import { useFetchBranchQuery } from "@/features/organizations/api/branchApi";
import { skipToken } from "@reduxjs/toolkit/query";

const DineModeDrawer = () => {
  const dispatch = useAppDispatch();
  const { organization } = useContext(WebsiteContext);
  const isDineModeOpen = useAppSelector((store) => store.ui.isDineModeOpen);
  const selectedDineMode = useAppSelector((store) => store.order.flow);
  const organizationId = useAppSelector((state) => state.ui.activeOrganization);

  const handleClose = () => {
    dispatch(setIsDineModeOpen(false));
  };

  const handleModeSelection = (mode: MENU_FLOWS) => {
    dispatch(setOrdersFlow(mode));
    localStorage.setItem(LocalStorage.DINING_MODE, mode);
    handleClose();
  };

  const organizationBranch = useFetchBranchQuery(organizationId ?? skipToken, {
    skip: !organizationId,
  });
  const branches = organizationBranch?.data && Array.isArray(organizationBranch.data) ? organizationBranch.data : [];
  const hasBranches = branches.length > 0;
  const activeBranch = useAppSelector((state) => state.ui.activeBranch);

  const dineModes = useMemo(() => {
    const lsBranchId = localStorage.getItem(LocalStorage.BRANCH_ID);
    const branchToUse = branches.find((b) => b.id === activeBranch)
      || branches.find((b) => b.id === lsBranchId);

    return DINING_MODES.filter((mode) => {
      if (hasBranches && branchToUse) {
        const hasService = branchToUse.services?.includes(mode.id);
        const isBranchOpen = checkServiceAvailability({
          isEnabled: true,
          schedule: branchToUse.schedule,
        });
        return hasService && isBranchOpen;
      }

      const serviceConfig = get(organization, `settings.services.${camelCase(mode.id)}`);
      return checkServiceAvailability(serviceConfig);
    });
  }, [organization, hasBranches, activeBranch, branches]);

  useEffect(() => {
    const lsDineMode = localStorage.getItem(LocalStorage.DINING_MODE);
    const dineModeIds = dineModes.map((mode) => mode.id);

    if (!selectedDineMode && lsDineMode && dineModeIds.includes(lsDineMode as MENU_FLOWS)) {
      dispatch(setOrdersFlow(lsDineMode as MENU_FLOWS));
    }

    if ((!selectedDineMode && !lsDineMode) || (selectedDineMode && !dineModeIds.includes(selectedDineMode))) {
      dispatch(setOrdersFlow(null));
    }
  }, [selectedDineMode]);



  return (
    <Drawer anchor="bottom" open={isDineModeOpen} onClose={handleClose}>
      <Stack px={2} py={3} direction="row" spacing={2} alignItems="center" justifyContent="space-between">
        <Typography variant="h4">¿Cómo deseas disfrutar tu pedido hoy?</Typography>
        <IconButton onClick={handleClose}>
          <HugeiconsIcon icon={Cancel01Icon} />
        </IconButton>
      </Stack>

      <Divider />

      <Stack px={2} py={2} pb={4}>
        {dineModes.map((mode) => (
          <ButtonBase
            key={mode.id}
            onClick={() => handleModeSelection(mode.id)}
            sx={{
              borderRadius: 3,
              backgroundColor: selectedDineMode === mode.id ? mode.color[50] : "transparent",
            }}
          >
            <Stack direction="row" alignItems="center" flex={1} spacing={3} p={2}>
              <Avatar
                sx={{
                  backgroundColor: mode.color[500],
                  padding: 1.5,
                  width: "50px",
                  height: "50px",
                }}
              >
                <HugeiconsIcon size={32} icon={mode.icon} strokeWidth={1} />
              </Avatar>
              <Stack alignItems="flex-start" justifyContent="center">
                <Typography textAlign="left" variant="h4" fontWeight={500}>
                  {mode.title}
                </Typography>
                <Typography textAlign="left" variant="body1">
                  {mode.description}
                </Typography>
              </Stack>
            </Stack>
          </ButtonBase>
        ))}
      </Stack>
    </Drawer>
  );
};

export default DineModeDrawer;
